import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { FACEBOOK_URL } from "../site";

const links = [
  { label: "Accueil", href: "#accueil" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Tarifs", href: "#tarifs", testid: "nav-link-tarifs" },
  { label: "Notre Méthode", href: "#methode", testid: "nav-link-methode" },
  { label: "Avis", href: "#avis", testid: "nav-link-avis" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200/80 bg-white/85 shadow-sm backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#accueil" data-testid="nav-logo" aria-label="PureTextiles — Accueil">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid || "nav-link-accueil"}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-teal-700"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-facebook-btn"
            className="hidden items-center gap-2 rounded-full bg-[#1877F2] px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-900/10 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#166FE5] hover:shadow-lg sm:inline-flex"
          >
            <Facebook className="h-4 w-4" />
            Nous contacter
          </a>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-slate-700 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-b border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-teal-50 hover:text-teal-700"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#1877F2] px-4 py-3 text-base font-semibold text-white"
              >
                <Facebook className="h-5 w-5" />
                Nous contacter sur Facebook
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
